'use client'

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react'
import apiClient, { acquireToken } from '@/lib/noc-api-client'

export interface CircuitBreakerState {
  circuit_break_active: boolean
  reason: string | null
  triggered_at: string | null
  email_summary: string | null
  triggered_by_user: string | null
  soc_reachable: boolean
  public_ip: string | null
  live_is_active: boolean | null
  protected_apps_before: Array<{ name: string; status: string }>
  protected_apps_after: Array<{ name: string; status: string }>
  agents_suspended: boolean
  suspended_at: string | null
  suspended_agents: Array<{ id: string; name: string }>
  loaded: boolean
  verifying: boolean
}

export interface StatusEvent {
  type: string
  [key: string]: unknown
}

interface CircuitBreakerContextType extends CircuitBreakerState {
  verify: () => Promise<{ restored: boolean }>
  refresh: () => Promise<void>
  /** Last raw event pushed by the backend SSE stream (null until first event). */
  lastStatusEvent: StatusEvent | null
}

const defaultState: CircuitBreakerState = {
  circuit_break_active: false,
  reason: null,
  triggered_at: null,
  email_summary: null,
  triggered_by_user: null,
  soc_reachable: true,
  public_ip: null,
  live_is_active: null,
  protected_apps_before: [],
  protected_apps_after: [],
  agents_suspended: false,
  suspended_at: null,
  suspended_agents: [],
  loaded: false,
  verifying: false,
}

const CircuitBreakerContext = createContext<CircuitBreakerContextType>({
  ...defaultState,
  verify: async () => ({ restored: false }),
  refresh: async () => {},
  lastStatusEvent: null,
})

export function CircuitBreakerProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CircuitBreakerState>(defaultState)
  const [lastStatusEvent, setLastStatusEvent] = useState<StatusEvent | null>(null)

  // Keep a ref to the abort controller so we can cancel the SSE fetch on unmount
  const sseAbortRef = useRef<AbortController | null>(null)

  const refresh = useCallback(async () => {
    try {
      const data = await apiClient('/api/circuit-breaker-status')
      setState(prev => ({
        ...prev,
        circuit_break_active: data.circuit_break_active ?? false,
        reason: data.reason ?? null,
        triggered_at: data.triggered_at ?? null,
        email_summary: data.email_summary ?? null,
        triggered_by_user: data.triggered_by_user ?? null,
        soc_reachable: data.soc_reachable ?? true,
        public_ip: data.public_ip ?? null,
        live_is_active: data.live_is_active ?? null,
        protected_apps_before: data.protected_apps_before ?? [],
        protected_apps_after: data.protected_apps_after ?? [],
        agents_suspended: data.agents_suspended ?? false,
        suspended_at: data.suspended_at ?? null,
        suspended_agents: data.suspended_agents ?? [],
        loaded: true,
      }))
    } catch {
      setState(prev => ({ ...prev, loaded: true, circuit_break_active: false }))
    }
  }, [])

  const verify = useCallback(async (): Promise<{ restored: boolean }> => {
    setState(prev => ({ ...prev, verifying: true }))
    try {
      const data = await apiClient('/api/circuit-breaker-verify', { method: 'POST' })
      const restored = data.restored === true
      // Backend invalidates the cache on verify so this call goes straight to
      // Symphony Trust for accurate data.
      await refresh()
      setState(prev => ({ ...prev, verifying: false }))
      return { restored }
    } catch {
      setState(prev => ({ ...prev, verifying: false }))
      return { restored: false }
    }
  }, [refresh])

  // ---------------------------------------------------------------------------
  // SSE: connect to /api/status/stream and react instantly to pushed events.
  // Replaces polling — the backend pushes whenever:
  //   • Cisco AI Defense blocks a request   → "cisco_ai_defense_block"
  //   • NOC agents are suspended            → "agents_suspended"
  //   • NOC agents are resumed              → "agents_resumed"
  //   • Circuit break triggered             → "circuit_break_triggered"
  //   • Circuit break resolved              → "circuit_break_resolved"
  //
  // Auto-reconnects with exponential back-off (1 s → 2 s → 4 s … capped at 30 s).
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false
    let retryDelay = 1_000
    let retryTimer: ReturnType<typeof setTimeout> | null = null

    const connect = async () => {
      if (cancelled) return

      const controller = new AbortController()
      sseAbortRef.current = controller

      try {
        const token = await acquireToken()
        if (!token || cancelled) return

        const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? ''
        const baseUrl =
          process.env.NEXT_PUBLIC_NOC_API_URL ??
          (typeof window !== 'undefined' ? window.location.origin : '')
        const url = `${baseUrl}${basePath}/api/status/stream`

        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        })

        if (!res.ok || !res.body) throw new Error(`SSE unavailable (${res.status})`)

        // Successful connection — reset back-off
        retryDelay = 1_000

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buf = ''

        while (!cancelled) {
          const { done, value } = await reader.read()
          if (done) break
          buf += decoder.decode(value, { stream: true })
          const lines = buf.split('\n')
          buf = lines.pop() ?? ''

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            try {
              const evt: StatusEvent = JSON.parse(line.slice(6))

              if (evt.type === 'cisco_ai_defense_block') {
                // Optimistic update: we know suspension is being triggered right now.
                // Show it immediately — the subsequent "agents_suspended" event will
                // trigger a real refresh() with the actual Symphony Trust state.
                setState(prev => ({
                  ...prev,
                  agents_suspended: true,
                  suspended_at: new Date().toISOString(),
                }))
                setLastStatusEvent(evt)
              } else if (evt.type === 'agents_resumed') {
                // agents_resumed is only published AFTER Phase 3 verification confirms
                // all agents are active in Symphony Trust — safe to update immediately.
                setState(prev => ({
                  ...prev,
                  agents_suspended: false,
                  suspended_at: null,
                  suspended_agents: [],
                }))
                setLastStatusEvent(evt)
                // Background refresh to sync any other fields (circuit_break_active etc.)
                refresh()
              } else if (evt.type === 'agents_suspended') {
                // Optimistic update: show suspended immediately so the UI
                // flips to "not operational" without waiting for the refresh()
                // round-trip — mirrors the pattern used by agents_resumed.
                setState(prev => ({
                  ...prev,
                  agents_suspended: true,
                  suspended_at: (evt.suspended_at as string) || new Date().toISOString(),
                }))
                setLastStatusEvent(evt)
                refresh()
              } else if (evt.type === 'circuit_break_triggered' || evt.type === 'circuit_break_resolved') {
                // Circuit breaker triggered or resolved (email command, manual SOC, DSPA, verify)
                // Fetch fresh data so sidebar + dashboard update instantly
                refresh()
                setLastStatusEvent(evt)
              }
              // 'connected' and keepalive comments are silently ignored
            } catch {
              /* skip malformed lines */
            }
          }
        }
      } catch (err) {
        if ((err as Error)?.name === 'AbortError' || cancelled) return
        // Reconnect with exponential back-off
        retryTimer = setTimeout(() => {
          retryDelay = Math.min(retryDelay * 2, 30_000)
          connect()
        }, retryDelay)
      }
    }

    // Kick off the initial load + SSE connection in parallel
    refresh()
    connect()

    return () => {
      cancelled = true
      sseAbortRef.current?.abort()
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [refresh])

  return (
    <CircuitBreakerContext.Provider value={{ ...state, verify, refresh, lastStatusEvent }}>
      {children}
    </CircuitBreakerContext.Provider>
  )
}

export function useCircuitBreaker() {
  return useContext(CircuitBreakerContext)
}
